import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragStartEvent, DragEndEvent, DragOverEvent } from '@dnd-kit/core';
import { useDesignerStore } from './store';
import {
  createNodeFromCatalog,
  COMPONENT_CATALOG,
  collectRowIds,
  flattenTree,
  getPreset,
  reportDefinitionV2Schema,
} from '@calculom/report-designer';
import type { RowNode } from '@calculom/report-designer';
import { trpc } from '../../../lib/trpc';
import { DesignerToolbar } from './components/DesignerToolbar';
import { ComponentPalette } from './components/ComponentPalette';
import { Canvas } from './components/Canvas';
import { RightSidebar } from './components/RightSidebar';
import { ResizeHandle } from './components/ResizeHandle';
import { ColumnConfigModal } from './components/ColumnConfigModal';

const MIN_PANEL = 150;
const DEFAULT_LEFT = 224;   // w-56
const DEFAULT_RIGHT = 288;  // w-72

export interface DropIndicator {
  targetId: string;
  position: 'before' | 'after';
}

export function ReportDesignerPage() {
  const { templateId: urlTemplateId } = useParams<{ templateId?: string }>();
  const [searchParams] = useSearchParams();
  const presetId = searchParams.get('preset');
  const {
    nodes,
    addNode,
    moveNode,
    selectedRowId,
    indentNode,
    outdentNode,
    loadTemplate,
    templateId: storeTemplateId,
  } = useDesignerStore();
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [dragLabel, setDragLabel] = useState<string>('');
  const [leftWidth, setLeftWidth] = useState(DEFAULT_LEFT);
  const [rightWidth, setRightWidth] = useState(DEFAULT_RIGHT);
  const [dropIndicator, setDropIndicator] = useState<DropIndicator | null>(null);
  const [templateLoadError, setTemplateLoadError] = useState<string | null>(null);

  // Load template from API when URL has a templateId
  const { data: templateData, error: templateQueryError } = trpc.fra.getTemplate.useQuery(
    { id: urlTemplateId ?? '' },
    { enabled: !!urlTemplateId && storeTemplateId !== urlTemplateId },
  );

  useEffect(() => {
    if (templateData && urlTemplateId && storeTemplateId !== urlTemplateId) {
      const parsed = reportDefinitionV2Schema.safeParse(templateData.templateData);
      if (!parsed.success) {
        console.error('Invalid report template payload received from API', parsed.error.flatten());
        setTemplateLoadError('This report template is invalid and could not be loaded.');
        return;
      }

      setTemplateLoadError(null);
      const { nodes: savedNodes, columnConfig: savedColumnConfig, layoutSettings: savedLayoutSettings, ...definition } = parsed.data;
      loadTemplate(definition, urlTemplateId);
      useDesignerStore.setState({
        nodes: savedNodes as RowNode[],
        columnConfig: savedColumnConfig,
        layoutSettings: savedLayoutSettings,
      });
    }
  }, [templateData, urlTemplateId, storeTemplateId, loadTemplate]);

  if (templateQueryError || templateLoadError) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-100 p-6">
        <div className="max-w-md w-full rounded-lg border border-red-200 bg-white p-6 shadow-sm">
          <h1 className="text-lg font-semibold text-gray-900">Unable to load report template</h1>
          <p className="mt-2 text-sm text-gray-600">
            {templateLoadError ?? templateQueryError?.message ?? 'The template could not be loaded.'}
          </p>
        </div>
      </div>
    );
  }

  // Load preset when URL has ?preset= query parameter
  const presetLoadedRef = useRef(false);
  useEffect(() => {
    if (!presetId || urlTemplateId || presetLoadedRef.current) return;
    const preset = getPreset(presetId);
    if (!preset) return;

    presetLoadedRef.current = true;
    // Set all preset state in a single atomic update to avoid race conditions
    // where loadTemplate resets nodes to [] before the second setState can set them
    useDesignerStore.setState({
      definition: { ...preset.definition },
      nodes: preset.nodes,
      columnConfig: preset.columnConfig,
      layoutSettings: { ...useDesignerStore.getState().layoutSettings },
      selectedRowId: null,
      selectedColumnId: null,
      columnModalOpen: false,
      isDirty: true,
      templateId: null,
      instructionText: preset.instructionText ?? null,
    });
  }, [presetId, urlTemplateId]);

  const handleLeftResize = useCallback((delta: number) => {
    setLeftWidth((w) => Math.max(MIN_PANEL, w + delta));
  }, []);

  const handleRightResize = useCallback((delta: number) => {
    setRightWidth((w) => Math.max(MIN_PANEL, w - delta));
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    const id = String(event.active.id);
    setActiveDragId(id);

    const catalogItem = COMPONENT_CATALOG.find((c) => c.id === id);
    if (catalogItem) {
      setDragLabel(catalogItem.name);
    } else {
      const flat = flattenTree(nodes);
      const node = flat.find((n) => n.id === id);
      setDragLabel(node?.data.type ?? 'Row');
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) {
      setDropIndicator(null);
      return;
    }

    const activeData = active.data.current;
    if (activeData?.type !== 'palette-item') {
      setDropIndicator(null);
      return;
    }

    if (over.id === 'canvas-droppable') {
      setDropIndicator(null);
      return;
    }

    const overRect = over.rect;
    const activeTranslated = active.rect.current.translated;
    if (!overRect || !activeTranslated) {
      setDropIndicator(null);
      return;
    }

    const overMidY = overRect.top + overRect.height / 2;
    const activeCenterY = activeTranslated.top + activeTranslated.height / 2;
    const position = activeCenterY < overMidY ? 'before' : 'after';

    setDropIndicator((current) => {
      if (current?.targetId === String(over.id) && current?.position === position) {
        return current;
      }
      return { targetId: String(over.id), position };
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const currentIndicator = dropIndicator;
    setActiveDragId(null);
    setDragLabel('');
    setDropIndicator(null);
    const { active, over } = event;
    if (!over) return;

    const activeData = active.data.current;

    // Palette item dropped onto canvas
    if (activeData?.type === 'palette-item') {
      const existingRowIds = collectRowIds(nodes);
      const newNode = createNodeFromCatalog(activeData.catalogId, existingRowIds);
      if (!newNode) return;

      const flat = flattenTree(nodes);
      const overNode = flat.find((n) => n.id === over.id);

      if (overNode && currentIndicator?.position === 'before') {
        const overIndex = flat.findIndex((n) => n.id === over.id);
        if (overIndex > 0) {
          addNode(newNode, flat[overIndex - 1]!.id);
        } else {
          addNode(newNode, '__prepend__');
        }
      } else if (overNode) {
        addNode(newNode, String(over.id));
      } else {
        addNode(newNode);
      }
      return;
    }

    // Reordering existing rows
    if (active.id !== over.id) {
      moveNode(String(active.id), String(over.id));
    }
  };

  // Use a ref to access the latest selectedRowId inside the event listener
  const selectedRowIdRef = useRef(selectedRowId);
  useEffect(() => {
    selectedRowIdRef.current = selectedRowId;
  }, [selectedRowId]);

  // Keyboard shortcuts: Tab to indent, Shift+Tab to outdent
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!selectedRowIdRef.current) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      if (e.key === 'Tab') {
        e.preventDefault();
        if (e.shiftKey) {
          outdentNode(selectedRowIdRef.current);
        } else {
          indentNode(selectedRowIdRef.current);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [indentNode, outdentNode]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="h-screen flex flex-col bg-gray-100">
        <DesignerToolbar />
        <div className="flex flex-1 overflow-hidden">
          <ComponentPalette width={leftWidth} />
          <ResizeHandle onResize={handleLeftResize} />
          <Canvas dropIndicator={dropIndicator} />
          <ResizeHandle onResize={handleRightResize} />
          <RightSidebar width={rightWidth} />
        </div>
      </div>

      <ColumnConfigModal />

      <DragOverlay>
        {activeDragId ? (
          <div className="px-3 py-2 bg-white border-2 border-blue-400 rounded shadow-lg text-sm opacity-90">
            {dragLabel}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
